import _ from "utilities/vendor/lodash/4.13.1/js/lodash.custom.min";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.0.4/js/sparta-store-utility";
import Transport from "utilities/global/sparta-transport-utility/4.0.2/js/transport";
/* global spartaStoreUtility _ Transport */
const CACHE_PATH_NAMESPACE = 'transport-cache:';
const ps = new WeakMap();

/**
 * if ttl is 0, use sessionStorage
 * if ttl is greater than 1, use localStorage
 * if ttl is 1 use spartaStoreUtility
 * @param {object} requestObj e.g. { ttl: 0, cacheKey: 'mykey' }
 */
function setCache(requestObj = {}) {
  const ttl = parseInt(requestObj.ttl, 10);
  const cacheKey = `${CACHE_PATH_NAMESPACE}${requestObj.cachePath}`;
  const storageType = (ttl === 0) ? 'sessionStorage' : 'localStorage';

  return (ttl === 1 && spartaStoreUtility.addData(cacheKey, requestObj)) ||
    spartaStoreUtility.persist(cacheKey, requestObj, storageType);
}

/**
 * processJWTBeforeSend
 * @param {object} jwt JWT configuration
 * @param {object} xhr XHR object
 * @param {object} options Options
 */
function processJWTBeforeSend(jwt, xhr) {
  const {
    name: tokenName,
    result: tokenResult,
  } = jwt;

  // getting all jwt tokens
  const tokens = spartaStoreUtility.getPersisted('jwt');

  // looking for existing token
  const JWToken = tokens[tokenResult || tokenName];

  // attach token if it exists
  if (JWToken) xhr.setRequestHeader('Authorization', `Bearer ${JWToken}`);
}

/**
 * processJWTOnSuccess
 * @param {object} jwt JWT configuration
 * @param {*} data XHR response data
 * @param {string} status XHR response status
 * @param {object} xhr XHR object
 */
function processJWTOnSuccess(jwt, data) {
  const {
    name: tokenName,
    lookup: tokenLookup,
    result: tokenResult,
  } = jwt;

  // look for token based on lookup path (if it exists) otherwise look up by tokenName
  const JWToken = _.isString(tokenLookup)
    ? _.get(data, tokenLookup)
    : data[tokenName];

  // cache the jwt token if it comes back in the response
  if (JWToken) {
    // getting all jwt tokens
    const tokens = spartaStoreUtility.getPersisted('jwt');
    const existingToken = tokens[tokenResult || tokenName];

    // check for existing token matching the token in the response
    if (existingToken !== JWToken) {
      // save the token
      tokens[tokenResult || tokenName] = JWToken;

      // persist jwt contents
      spartaStoreUtility.persist('jwt', tokens);
    }
  }
}

/**
 * @param {string} method e.g. 'GET'
 * @param {string} url e.g. 'https://bankofamerica.com/move-money/one-time-transfer/REST-service'
 * @return {string} e.g. 'GET.move-money.one-time-transfer.REST-service'
 */
function getCachePath(method, url) {
  const urlObj = Transport.getUrlInfo(url);
  const path = url.match(/(?:https?:\/\/[^\/]*\/)(.*)/) || false;
  return (path ? `${method}.${urlObj.host.replace(':', '.')}${urlObj.pathname.replace(/\//g, '.')}${urlObj.search}` : '');
}

class SpartaTransportUtility {
  /**
   * Performs an AJAX request and caches the result
   * @param {object} subscription
   * @param {object} subscription.data an object to be set to the URL as data
   * @param {boolean} subscription.cache http://api.jquery.com/jquery.ajax/
   * @param {boolean|string} subscription.contentType http://api.jquery.com/jquery.ajax/
   * @param {boolean} subscription.crossDomain http://api.jquery.com/jquery.ajax/
   * @param {string} subscription.dataType http://api.jquery.com/jquery.ajax/
   * @param {function} subscription.error http://api.jquery.com/jquery.ajax/
   * @param {object} subscription.headers http://api.jquery.com/jquery.ajax/
   * @param {object} subscription.jwt JWT configuration object
   * @param {string} subscription.jwt.name The key name the JWT will be sent as
   * @param {string} subscription.jwt.lookup The lookup path for finding the JWT from response, will be attached to the next request
   * @param {string} subscription.jwt.result The key name the JWT will be persisted with
   * @param {string} subscription.method http://api.jquery.com/jquery.ajax/
   * @param {function} subscription.success http://api.jquery.com/jquery.ajax/ fires when a successful request/cache pull is complete. It will be passed the result/cached data
   * @param {number} subscription.timeout http://api.jquery.com/jquery.ajax/
   * @param {string} subscription.url the URL/REST object to make a request to
   * @param {object} subscription.xhrFields http://api.jquery.com/jquery.ajax/
   * @return {boolean} false if not enough data exists for ajax request
   */
  constructor(sub = {}) {
    if (!spartaStoreUtility.getPersisted('jwt')) spartaStoreUtility.persist('jwt', {});

    const defaults = {
      error: () => { /* noop */ }, // eslint-disable-line no-unused-vars
      cache: true,
      jwt: {},
      method: 'GET',
      success: () => { /* noop */ },
      url: window.location.href,
      ignorePreload: false,
    };

    const subscription = { ...defaults, ...sub };
    const { beforeSend, cache, jwt, method, success, url, ignorePreload } = subscription;

    // request object
    const requestObj = {
      cachePath: getCachePath(method, url),
      subscription,
    };

    // Get cached data
    const cachedData = this.isCached(requestObj);
    const preloaded = this.isPreloaded(requestObj);

    const transportOptions = {
      config: {
        ...defaults,
        ...subscription,
        beforeSend(xhr, options) {
          // process jwt before sending request
          processJWTBeforeSend(jwt, xhr, options);
          // call user defined callback
          if (beforeSend && typeof beforeSend === 'function') return beforeSend.call(this, xhr, this);
        },
        success(d, status, xhr) {
          const data = d;

          // process jwt on success of request
          if (data) processJWTOnSuccess(jwt, data, status, xhr);

          // time to live
          const ttl = xhr.getResponseHeader('ttl');
          Object.assign(requestObj, { data, ttl, timestamp: Date.now() });

          // false for NaN
          if (parseInt(ttl, 10) >= 0 && cache !== false) setCache(requestObj);

          // call the user defined success callback
          if (success && typeof success === 'function') success.call(this, data, status, xhr);
        },
      },
      globalConfig: ps.get(SpartaTransportUtility),
    };

    ps.set(this, { transportOptions });

    // has this been kicked off by a webworker?
    if (preloaded && !ignorePreload) return false;

    if (cachedData) {
      success.call(this, cachedData.data);
    } else {
      ps.get(this).transport = new Transport(
        { ...transportOptions.config },
        transportOptions.globalConfig,
      );
    }

    return this;
  }

  /**
   * abort
   * Abort a transport
   */
  abort() {
    ps.get(this).transport.abort();
  }

  /**
   * List of events
   */
  get EVENTS() { // eslint-disable-line class-methods-use-this
    return {
      CREATE_EVENT: 'TRANSPORT.createdTransport',
      UPDATE_EVENT: 'TRANSPORT.updatedTransport',
      DELEGATE_EVENT: 'TRANSPORT.delegatedTransport',
      FETCH_EVENT: 'TRANSPORT.fetchedTransport',
      REFRESH_EVENT: 'TRANSPORT.refreshedTransport',
    };
  }

  /**
   * @param {object} requestObj e.g. { ttl: 0, cachePath: 'GET.move-money.one-time-transfer.REST-service' }
   * @return {*} result will be null if data not cached and will
   * be the stored data if it is cached
   */
  isCached(requestObj = {}) { // eslint-disable-line class-methods-use-this
    const cacheKey = CACHE_PATH_NAMESPACE + requestObj.cachePath;
    let storedData = spartaStoreUtility.getData(cacheKey) || spartaStoreUtility.getPersisted(cacheKey, 'sessionStorage');
    let result;

    storedData = Array.isArray(storedData) ? storedData[0] : storedData;

    if (!storedData) {
      return null;
    }

    // i.e. 0 (session) || 1 (page)
    if (storedData.ttl <= 1) {
      result = storedData;
    } else {
      const currentTime = Date.now();
      const cachedTime = storedData.timestamp;
      const diffTime = (currentTime - cachedTime) / 1000;

      // compare the ttls to see
      result = diffTime < storedData.ttl ? storedData : null;
    }

    return result;
  }

  /**
   * isPreloded
   * @param {any} [requestObj={}]
   * @returns {boolean} false if not part of the api-preload object, true if it is
   *
   * @memberOf SpartaTransportUtility
   */
  isPreloaded(requestObj = {}) { // eslint-disable-line class-methods-use-this
    window.sparta = window.sparta || {};
    window.sparta.APIWorkers = window.sparta.APIWorkers || [];

    const workers = window.sparta.APIWorkers;
    const worker = _.find(workers, { key: requestObj.cachePath });

    if (!worker) return false;

    const onError = (e) => {
      const transportOptions = ps.get(this).transportOptions;

      ps.get(this).transport = new Transport(
        { ...transportOptions.config },
        transportOptions.globalConfig,
      );
    };

    if (!worker.data) {
      document.addEventListener(`${requestObj.cachePath}:TIMEOUT`, onError, false);
      document.addEventListener(`${requestObj.cachePath}:ERROR`, onError, false);
      document.addEventListener(`${requestObj.cachePath}:LOADED`, () =>
        requestObj.subscription.success(worker.data), false);
    } else {
      // return the response
      requestObj.subscription.success(worker.data);
    }

    return !!worker;
  }
}

/**
 * getGlobalConfig
 * Get the global config
 */
export function getGlobalConfig() {
  let config = ps.get(SpartaTransportUtility);
  if (!config) {
    ps.set(SpartaTransportUtility, { options: {} });
    config = ps.get(SpartaTransportUtility);
  }
  return config;
}

/**
 * globalHandler
 * @param {string} lookup Global handler lookup name
 */
function globalHandler(lookup) {
  return (doc, cb) => {
    const globalConfig = getGlobalConfig();
    globalConfig[`global${lookup}`] = { doc, cb };
  };
}

/**
 * globalError
 * Transport global error handler
 */
export const globalError = globalHandler('Error');

/**
 * globalSuccess
 * Transport global success handler
 */
export const globalSuccess = globalHandler('Success');

/**
 * globalComplete
 * Transport global complete handler
 */
export const globalComplete = globalHandler('Complete');

/**
 * setup
 * @param {options} options Transport option configuration
 */
export function setup(options = {}) {
  getGlobalConfig().options = options;
}

/**
 * subscribe
 * @param {object} options Same options as the SpartaTransportUtility
 */
export function subscribe(options = {}) {
  const extendedOptions = { ...getGlobalConfig().options, ...options };
  return new SpartaTransportUtility(extendedOptions);
}

/**
 * Performs an AJAX request and returns the result ( takes the same arguments as subscribe() )
 * Essentially just a wrapper for subscribe
 * Same options as the SpartaTransportUtility
 * @param {object} subscription
*/
export function once(options = {}) {
  const req = options;
  req.cache = false;
  return this.subscribe(req);
}

export default {
  globalError,
  globalComplete,
  globalSuccess,
  getGlobalConfig,
  once,
  setup,
  subscribe,
};
